import { useCallback } from "react";
import { parseAsStringLiteral, useQueryState } from "nuqs";

export const MODAL_NAMES = [
  "strategies",
  "notifications",
  "search",
  "guide",
  "upgrade",
  "library",
] as const;
export type ModalName = (typeof MODAL_NAMES)[number];

/**
 * Single source of truth for which global modal/panel is open.
 * Backed by `?modal=` so state survives refresh and is shareable.
 *
 * Only one modal can be open at a time — opening a new one closes the
 * previous one implicitly via the URL replacement.
 */
export function useModalRouter() {
  const [modal, setModalRaw] = useQueryState(
    "modal",
    parseAsStringLiteral(MODAL_NAMES),
  );

  const openModal = useCallback(
    (name: ModalName) => void setModalRaw(name),
    [setModalRaw],
  );
  const closeModal = useCallback(() => void setModalRaw(null), [setModalRaw]);
  const setOpen = useCallback(
    (name: ModalName, open: boolean) => void setModalRaw(open ? name : null),
    [setModalRaw],
  );

  return { modal, openModal, closeModal, setOpen };
}

/** Convenience: read whether a specific modal is open. */
export function useIsModalOpen(name: ModalName): boolean {
  const { modal } = useModalRouter();
  return modal === name;
}
